import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import FilterItemList from './FilterItemList';
import { trapFocus } from '../utils/trapFocus';
import { useShowFilterEvent } from '../hooks/useShowFilterEvent';
import { useActiveFilters } from '../hooks/useActiveFilters';
import useIsSmallScreen from '../hooks/useIsSmallScreen';

const FilterModalContent = ({ isReady }) => {
	const [animationName, setAnimationName] = useState(null);
	const labels = useLabels();
	const closeButtonRef = useRef(null);
	const deActivateFocusTrapRef = useRef(null);

	const { showFilter: showModal, hideFilter } = useShowFilterEvent(isReady);
	const { filters } = useActiveFilters(isReady);

	const hideModal = () => {
		setAnimationName(null);
		if (deActivateFocusTrapRef.current) {
			deActivateFocusTrapRef.current();
			deActivateFocusTrapRef.current = null;
		}
		hideFilter();
	};

	const onEntered = (modalNode) => {
		if (modalNode) {
			setTimeout(() => setAnimationName('slide'), 50);
			deActivateFocusTrapRef.current = trapFocus(modalNode);
			closeButtonRef?.current?.focus();
		}
	};

	return (
		<Modal
			show={showModal}
			onHide={hideModal}
			onEntered={onEntered}
			className={setClassNames(['dock-full', animationName])}
			aria-modal="true"
			aria-labelledby="ws-locations-filters-modal-label"
		>
			<Modal.Body>
				{/* Header with close icon on right */}
				<div className="d-flex align-items-center border-bottom pb-4">
					<h2
						id="ws-locations-filters-modal-label"
						className="ddc-font-size-large m-0"
					>
						{labels.get('FILTER_LOCATIONS')}
					</h2>
					<button
						type="button"
						className="chip chip-default border-0 p-0 ml-auto mr-3 flex-shrink-0"
						onClick={hideModal}
						ref={closeButtonRef}
						aria-label={labels.get('CLOSE')}
					>
						<i className="ddc-icon ddc-icon-remove2" />
					</button>
				</div>

				{/* Active filter items */}
				{filters.length > 0 && (
					<div className="py-4 border-bottom">
						<FilterItemList />
					</div>
				)}

				{/* Portal for ws-locations-facets */}
				<div
					className="ws-locations-facets-portal"
					data-portal="ws-locations-facets"
				/>
			</Modal.Body>
		</Modal>
	);
};

FilterModalContent.propTypes = {
	isReady: PropTypes.bool
};

FilterModalContent.defaultProps = {
	isReady: false
};

const FilterModal = ({ isReady }) => {
	const isSmallScreen = useIsSmallScreen();

	if (!isSmallScreen) {
		return null;
	}

	return <FilterModalContent isReady={isReady} />;
};

FilterModal.propTypes = {
	isReady: PropTypes.bool
};

FilterModal.defaultProps = {
	isReady: false
};

export default FilterModal;
